import {
  allEnabledIdentifiers,
  canIdentifyBy,
  CIO_SUBSCRIPTION_PREF_KEY,
  IdentifierKey,
  isAttributeEnabledIdentifier,
  RESTRICTED_ATTRIBUTES,
  type IdentifiersConfig,
} from '@/features/people';
import { isEmptyString } from '@/utils/string';
import type { MergedAttributes, ProfileSource } from '../types';

export type ProfileAttribute = {
  key: string;
  value: string;
};

const UNSUBSCRIBED = 'unsubscribed';

/**
 * The identifier names a profile lists, in the order the profile carries them,
 * plus `phone` when the workspace promotes phone: an empty phone row shows even
 * for a person without one.
 */
function displayedIdentifierKeysOf(
  profile: ProfileSource,
  identifiers: IdentifiersConfig | null | undefined,
): string[] {
  const keys = Object.keys(profile.identifiers ?? {}).filter((key) => isAttributeEnabledIdentifier(key, identifiers));
  if (canIdentifyBy(IdentifierKey.Phone, identifiers) && !keys.includes(IdentifierKey.Phone)) {
    keys.push(IdentifierKey.Phone);
  }
  return keys;
}

/**
 * A profile's attributes in display order: its identifiers first (shown even
 * when blank), then the reserved names, then the custom attributes
 * alphabetically. A `cio_id` with no value is dropped (single-identifier
 * workspaces list it as an identifier without returning it as an attribute),
 * and a non-identifier attribute shows only when it has a value.
 */
export function orderedProfileAttributes(
  profile: ProfileSource,
  identifiers: IdentifiersConfig | null | undefined,
): ProfileAttribute[] {
  const attributes = profile.attributes;
  const displayedIdentifierKeys = displayedIdentifierKeysOf(profile, identifiers);
  const profileIdentifierKeys = Object.keys(profile.identifiers ?? {});

  const customKeys = Object.keys(attributes)
    .filter(
      (key) =>
        !displayedIdentifierKeys.includes(key) &&
        !RESTRICTED_ATTRIBUTES.includes(key) &&
        key !== CIO_SUBSCRIPTION_PREF_KEY,
    )
    .sort();

  const allKeys = [...new Set([...displayedIdentifierKeys, ...RESTRICTED_ATTRIBUTES, ...customKeys])];

  return allKeys
    .filter(
      (key) => attributes[key] != null || profileIdentifierKeys.includes(key) || displayedIdentifierKeys.includes(key),
    )
    .map((key) => ({ key, value: attributes[key] ?? '' }))
    .filter(({ key, value }) => {
      if (key === IdentifierKey.CioId && isEmptyString(value)) {
        return false;
      }
      if (profileIdentifierKeys.includes(key) || displayedIdentifierKeys.includes(key)) {
        return true;
      }
      return !isEmptyString(value);
    });
}

/** Whether `key` is neither an identifier nor a reserved name. */
function isCustomAttribute(key: string, identifiers: IdentifiersConfig | null | undefined): boolean {
  return !(allEnabledIdentifiers(identifiers).includes(key) || RESTRICTED_ATTRIBUTES.includes(key));
}

/**
 * The attribute list a merge card shows. A profile that never set
 * `unsubscribed` is subscribed, so the list states that explicitly with a
 * `false` row placed where the reserved names end and the custom ones begin.
 * The merged card additionally moves the attributes contributed by the
 * secondary to the bottom, so what the merge adds reads as one block.
 */
export function mergeCardAttributes(
  profile: ProfileSource,
  identifiers: IdentifiersConfig | null | undefined,
  { fromSecondary }: { fromSecondary?: readonly string[] } = {},
): ProfileAttribute[] {
  let attributes = orderedProfileAttributes(profile, identifiers);

  if (!Object.keys(profile.attributes).includes(UNSUBSCRIBED)) {
    let position = attributes.findIndex((attribute) => isCustomAttribute(attribute.key, identifiers));
    if (position < 0) {
      position = attributes.length;
    }
    attributes = [
      ...attributes.slice(0, position),
      { key: UNSUBSCRIBED, value: 'false' },
      ...attributes.slice(position),
    ];
  }

  if (fromSecondary == null) {
    return attributes;
  }

  const primary = attributes.filter((attribute) => !fromSecondary.includes(attribute.key));
  const secondary = attributes.filter((attribute) => fromSecondary.includes(attribute.key));
  return [...primary, ...secondary];
}

/**
 * The merged attribute names that came from a profile other than `cioId`.
 *
 * Without a `cio_id` there is nothing to compare against: every attribute
 * would read as coming from elsewhere, annotating a whole card wrongly. An
 * unidentified profile therefore claims none of them, which leaves the card
 * unannotated rather than annotated backwards.
 */
export function attributeNamesFromOtherProfile(
  mergedAttributes: MergedAttributes,
  cioId: string | undefined,
): string[] {
  if (cioId == null || cioId === '') {
    return [];
  }
  return mergedAttributes.filter((attribute) => attribute.cio_id !== cioId).map((attribute) => attribute.name);
}

/**
 * The listed attributes the preview does not carry at all. A blank identifier
 * (an `id` that is null where email identifies people) is listed but never
 * merged, so it is marked as lost rather than kept.
 */
export function attributesNotPresentInMerge(
  attributes: readonly ProfileAttribute[],
  mergedAttributes: MergedAttributes,
): string[] {
  const mergedNames = mergedAttributes.map((attribute) => attribute.name);
  return attributes.filter((attribute) => !mergedNames.includes(attribute.key)).map((attribute) => attribute.key);
}

/** The attributes whose name contains the trimmed search term. */
export function filterAttributes(attributes: readonly ProfileAttribute[], searchTerm: string): ProfileAttribute[] {
  const term = searchTerm.trim();
  return attributes.filter((attribute) => attribute.key.includes(term));
}

/** The merged profile as an attribute source: the preview's values under the primary's identifiers. */
export function mergedProfileSource(mergedAttributes: MergedAttributes, primary: ProfileSource): ProfileSource {
  const attributes: Record<string, string> = {};
  for (const attribute of mergedAttributes) {
    attributes[attribute.name] = attribute.value;
  }
  return { attributes, identifiers: primary.identifiers };
}
